import { useEffect, useState } from "react";
import { getReferralStats } from "../api/api";
import { useAuth } from "../context/AuthContext";
import ReferralInfo from "../components/referral/ReferralInfo";
import ReferralStatus from "../components/referral/ReferralStatus";
import { Button } from "../components/ui/button";
import AuthModal from "../components/auth/AuthModal";
import { Helmet } from "react-helmet";
import {ReferralStats} from "../types";

const Referral = () => {
    const { user, isAuthenticated, isLoading: isAuthLoading } = useAuth();
    const [isAuthModalOpen, setAuthModalOpen] = useState(false);

    const [stats, setStats] = useState<ReferralStats | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);

    useEffect(() => {
        const fetchReferralStats = async () => {
            if (!user?.id || !isAuthenticated) return;

            setLoading(true);
            setError(false);
            try {
                const data = await getReferralStats(user.id);
                setStats(data);
            } catch (err) {
                setError(true);
            } finally {
                setLoading(false);
            }
        };

        fetchReferralStats();
    }, [user?.id, isAuthenticated]);

    return (
        <>
            <Helmet>
                <title>Refer & Earn | SurpriseTable</title>
                <meta
                    name="description"
                    content="Refer friends to SurpriseTable and earn rewards. Get €10 credit for each friend who signs up and makes a booking."
                />
            </Helmet>

            <section className="bg-background py-16 transition-colors duration-200 dark:bg-background">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    {isAuthLoading ? (
                        <div className="text-center py-16">Loading…</div>
                    ) : isAuthenticated ? (
                        <div className="overflow-hidden rounded-2xl bg-gradient-to-r from-red-50 to-orange-50 shadow-xl dark:from-gray-800 dark:to-gray-900">
                            <div className="md:flex">
                                <ReferralInfo />
                                <ReferralStatus stats={stats} loading={loading} error={error} />
                            </div>
                        </div>
                    ) : (
                        <div className="overflow-hidden rounded-2xl bg-gradient-to-r from-red-50 to-orange-50 shadow-xl dark:from-gray-800 dark:to-gray-900">
                            <div className="p-12 text-center">
                                <h2 className="mb-4 text-2xl font-bold text-gray-800 dark:text-white">
                                    Login to Access Your Referrals
                                </h2>
                                <p className="mb-8 text-gray-600 dark:text-gray-300">
                                    You need to be logged in to view and manage your referrals.
                                    Sign up or log in to start earning rewards!
                                </p>
                                <Button
                                    onClick={() => setAuthModalOpen(true)}
                                    className="bg-primary px-6 py-3 font-medium text-white hover:bg-red-700"
                                >
                                    Login / Register
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </section>

            <AuthModal
                isOpen={isAuthModalOpen}
                onClose={() => setAuthModalOpen(false)}
                initialView="login"
            />
        </>
    );
};

export default Referral;
